import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage } from 'react-intl';
import { ConnectionType, IConnectionDetailsDTO } from '../../../../../../api/connections.dao';
import { IScheduledZoomMeetingDetailsDTO, IZoomDAO, IZoomMeetingListItemDTO, ZoomMeetingType } from '../../../../../../api/zoom.dao';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { DateTimeFormatter } from '../../../../../../infrastructure/implementations/date-formatter';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../../../infrastructure/alerts';
import { CreateZoomMeetingFormDataVM } from './zoom-meeting-form-data.viewmodel';
import { isZoomMeetingReccuring } from '../../../../../../infrastructure/utils/connections.utils';
import { RECURRING_ZOOM_MEETING_ERROR } from '../../../../../../constants/zoom-meeting-connection.constant';
import { RecurringZoomMeetingFormDataVM } from './recurring-zoom-meeting-form/recurring-zoom-meeting-form-data.viewmodel';
import { AccountAdminSettingsType, IAccountAdminDAO, IRecurringZoomMeetingsDTO } from '../../../../../../api/account-admin';
import { ApprovalType } from '../_create-zoom-webinar-modal/create-zoom-webinar-form-data.viewmodel';
import { ConnectionVersion } from '../../../../../../constants/common.constants';

export interface IUpdateZoomMeetingDetails extends IScheduledZoomMeetingDetailsDTO {
    meetingConnectionId: number;
    label?: string;
    connectionHostKey?: string;
    dialForVs?: 'In' | 'Out' | null;
    connectionNotes?: string;
    connectionAccount?: string;
    connectionId?: string;
    connectionLink?: string;
    alternativeLink?: string;
    hostLink?: string;
    meetingUniqueReference?: number;
}

class ZoomMeetingVM {
    @mobx.observable
    private isFormShown: boolean;
    @mobx.observable
    private meetingId: number;
    @mobx.observable
    private currentZoomMeetingId: string;
    @mobx.observable
    private oldConnectionDetails: IConnectionDetailsDTO;
    @mobx.observable
    private editMode: boolean;
    @mobx.observable
    private isSaving: boolean;
    @mobx.observable
    private isRecurringMeetingSaving: boolean;
    @mobx.observable
    private isZoomIdInvalid: boolean;
    @mobx.observable
    private updateZoomMeetingCallback: (data: IConnectionDetailsDTO, shouldSwitchEditForm?: boolean) => void;
    @mobx.observable
    private connectionVersion: ConnectionVersion;
    @mobx.observable
    public createZoomMeetingFormDataVM: CreateZoomMeetingFormDataVM;
    @mobx.observable
    public recurringZoomMeetingFormDataVM: RecurringZoomMeetingFormDataVM;
    @mobx.observable
    public zoomMeetingType: ZoomMeetingType;
    @mobx.observable
    public isRecurringZoomMeetingFormShown: boolean;
    @mobx.observable
    public isLoadingAccountAdmin: boolean;
    constructor(
        private eventTimeZone: string,
        private zoomMeetingTopic: string,
        private startDateTime: Date,
        private endDateTime: Date,
        public readonly createZoomMeetingCallback: (
            zoomMeetingDetails?: IScheduledZoomMeetingDetailsDTO,
            showAutoRegAndAutoSyncToggles?: boolean
        ) => void,
        private readonly accountId?: number,
        private readonly eventId?: number,
        private readonly generalMeetingId?: number,
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly zoomDAO: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject private readonly accountAdminDAO: IAccountAdminDAO = IoC.Container.get(IAccountAdminDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
        this.isFormShown = false;
        this.editMode = false;
        this.isSaving = false;
        this.isRecurringMeetingSaving = false;
        this.isLoadingAccountAdmin = true;
        this.zoomMeetingType = ZoomMeetingType.SCHEDULED_MEETING;
        this.createZoomMeetingFormDataVM = new CreateZoomMeetingFormDataVM(
            eventTimeZone,
            zoomMeetingTopic,
            startDateTime,
            endDateTime
        );
        this.recurringZoomMeetingFormDataVM = new RecurringZoomMeetingFormDataVM(this.accountId);
    }

    public getRecurringZoomMeetingFormShownState = (): boolean => {
        return !this.getCurrentZoomMeetingId();
    };

    public getRecurringZoomMeetingsEnabledAndFormShown = (): boolean => {
        return this.getRecurringZoomMeetingFormShownState() && this.isRecurringZoomMeetingFormShown;
    };

    @mobx.action
    public showForm = (): void => {
        this.isFormShown = true;
        if (this.getRecurringZoomMeetingFormShownState()) {
            this.recurringZoomMeetingFormDataVM.cleanup();
            this.fetchAccountAdmin();
        }
    };

    @mobx.action
    public setEnableEditMode = (): void => {
        this.editMode = true;
    };

    @mobx.action
    public setDisableEditMode = (): void => {
        this.editMode = false;
    };

    @mobx.action
    public setUpdateZoomMeetingCallback = (callback): void => {
        this.updateZoomMeetingCallback = callback;
    };

    public getMeetingId = (): number => {
        return this.meetingId;
    };

    public getCurrentZoomMeetingId = (): string => {
        return this.currentZoomMeetingId;
    };

    public getOldConnectionDetails = (): IConnectionDetailsDTO => {
        return this.oldConnectionDetails;
    };

    @mobx.action
    public setOldConnectionDetails = (connection: IConnectionDetailsDTO): void => {
        this.oldConnectionDetails = connection;
    };

    @mobx.action
    public setMeetingId = (meetingId: number): void => {
        this.meetingId = meetingId;
    };

    @mobx.action
    public setCurrentZoomMeetingId = (currentZoomMeetingId: string): void => {
        this.currentZoomMeetingId = currentZoomMeetingId;
    };

    @mobx.action
    public hideForm = (): void => {
        this.createZoomMeetingFormDataVM.cleanup();
        this.recurringZoomMeetingFormDataVM.cleanup();
        this.setOldConnectionDetails(null);
        this.setMeetingId(null);
        this.setCurrentZoomMeetingId(null);
        this.isFormShown = false;
        this.zoomMeetingType = ZoomMeetingType.SCHEDULED_MEETING;
    };

    public createZoomMeetingHandle = async (): Promise<void> => {
        let zoomMeetingDetails: IScheduledZoomMeetingDetailsDTO;
        if (!this.createZoomMeetingFormDataVM.checkForm() || this.isSaving) {
            return;
        }

        mobx.runInAction(() => {
            this.isSaving = true;
        });
        const zoomMeeting = this.createZoomMeetingFormDataVM.getAPIDTO();

        try {
            const zoomMeetingId = (await this.zoomDAO.scheduleNewMeeting(zoomMeeting)).id;
            zoomMeetingDetails = await this.zoomDAO.getScheduledMeetingDetails(zoomMeetingId);
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError('Error', errorMessage);
            return;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
        this.createZoomMeetingCallback({
            ...zoomMeetingDetails,
            approvalType: zoomMeeting.settings.approvalType,
            registrantsConfirmationEmail: zoomMeeting.settings.registrantsConfirmationEmail,
            autoRegistrationEnabled: zoomMeeting.settings.autoRegistrationEnabled,
            autoSyncEnabled: zoomMeeting.settings.autoSyncEnabled,
        });
        this.hideForm();
    };

    public createRecurringZoomMeetingHandle = async (): Promise<void> => {
        let zoomMeetingDetails: IScheduledZoomMeetingDetailsDTO;
        if (!this.recurringZoomMeetingFormDataVM.checkForm() || this.isRecurringMeetingSaving) {
            return;
        }

        mobx.runInAction(() => {
            this.isRecurringMeetingSaving = true;
        });
        const zoomMeeting = this.recurringZoomMeetingFormDataVM.getAPIDTO();

        try {
            const zoomMeetingId = (await this.zoomDAO.scheduleNewRecurringMeeting(zoomMeeting)).id;
            zoomMeetingDetails = await this.zoomDAO.getScheduledMeetingDetails(zoomMeetingId);
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            this.alertsService.alert({
                renderCrossIcon: false,
                okButtonProps: {
                    text: 'Ok',
                    handler: () => this.hideForm(),
                },
                message: errorMessage,
            });
            return;
        } finally {
            mobx.runInAction(() => {
                this.isRecurringMeetingSaving = false;
            });
        }
        this.createZoomMeetingCallback({ ...zoomMeetingDetails });
        this.hideForm();
    };

    @mobx.action
    public setConnectionVersion(version: ConnectionVersion): void {
        this.connectionVersion = version;
    }

    public getConnectionVersion(): ConnectionVersion {
        return this.connectionVersion;
    }

    @mobx.action
    private setInitialValues = (data: IZoomMeetingListItemDTO): void => {
        this.createZoomMeetingFormDataVM.setTimeZone(data.timeZone);
        this.createZoomMeetingFormDataVM.setMeetingTopic(data.topic);
        this.createZoomMeetingFormDataVM.setStartDateTime(DateTimeFormatter.applyTimeZoneToDate(data.startTime, data.timeZone));
        this.createZoomMeetingFormDataVM.setEndDateTime(DateTimeFormatter.applyTimeZoneToDate(data.endTime, data.timeZone));
        this.createZoomMeetingFormDataVM.registrantsConfirmationEmail = data.registrantsConfirmationEmail;
        this.createZoomMeetingFormDataVM.approvalType = data.approvalType;

        if (data.approvalType === ApprovalType.AUTOMATICALLY || data.approvalType === ApprovalType.MANUALLY) {
            this.createZoomMeetingFormDataVM.registrationEnabled = true;
        }

        this.setMeetingId(data.id);
        this.setCurrentZoomMeetingId(data.zoomId);
    };

    private getScheduledMeetingDetails = async (zoomMeetingId: number): Promise<IScheduledZoomMeetingDetailsDTO> => {
        try {
            const details = await this.zoomDAO.getScheduledMeetingDetails(zoomMeetingId);
            return details;
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError('Error', errorMessage);
        }
    };

    private convertZoomMeetingInZoomConnection = (zoomMeetingDetails: IUpdateZoomMeetingDetails): IConnectionDetailsDTO => ({
        type: ConnectionType.Zoom,
        dialForVs: zoomMeetingDetails.dialForVs,
        connectionNotes: zoomMeetingDetails.connectionNotes,
        connectionHostKey: zoomMeetingDetails.connectionHostKey ?? zoomMeetingDetails.zoomUser?.hostKey,
        connectionAccount: zoomMeetingDetails.connectionAccount,
        connectionId: zoomMeetingDetails.connectionId ?? zoomMeetingDetails.zoomId,
        connectionLink: zoomMeetingDetails.connectionLink,
        alternativeLink: zoomMeetingDetails.alternativeLink,
        hostLink: zoomMeetingDetails.hostLink,
        connectionPassword: zoomMeetingDetails.password,
        meetingInvitation: zoomMeetingDetails.invitation,
        sip: zoomMeetingDetails.sip,
        connectionTopic: this.createZoomMeetingFormDataVM.getMeetingTopic() || zoomMeetingDetails.topic,
        label: zoomMeetingDetails.label,
        meetingConnectionId: zoomMeetingDetails.meetingConnectionId,
        autoRegistrationEnabled: zoomMeetingDetails.autoRegistrationEnabled,
        autoSyncEnabled: zoomMeetingDetails.autoSyncEnabled,
        meetingUniqueReference: zoomMeetingDetails.meetingUniqueReference
    });

    public getUpdatedMeetingDetails =
        async (generalMeetingId: number, accountId: number, eventId: number, meetingConnectionId: number): Promise<IConnectionDetailsDTO> => {
            try {
                const details = await this.zoomDAO.getUpdatedMeetingDetails({
                    meetingId: generalMeetingId,
                    accountId: accountId,
                    eventId: eventId,
                    meetingConnectionId: meetingConnectionId
                });
                return details;
            } catch (e) {
                const errorMessage = this.errorParsingService.extractMessage(e);
                alertServerError('Error', errorMessage);
            }
        };

    public updateZoomMeetingHandle = async (): Promise<void> => {
        let zoomMeetingDetails: IScheduledZoomMeetingDetailsDTO;
        if (!this.createZoomMeetingFormDataVM.checkForm() || this.isSaving) {
            return;
        }

        mobx.runInAction(() => {
            this.isSaving = true;
        });

        try {
            const meetingID = this.getMeetingId();
            let dataToAPI = this.createZoomMeetingFormDataVM.getAPIDTO();
            if (this.editMode) {
                delete dataToAPI.settings.autoRecording;
                delete dataToAPI.settings.autoSyncEnabled;
                delete dataToAPI.settings.autoRegistrationEnabled;
            }
            const { id: zoomMeetingId } = await this.zoomDAO.updateMeeting(meetingID, dataToAPI);
            zoomMeetingDetails = await this.getScheduledMeetingDetails(zoomMeetingId);
            zoomMeetingDetails.autoRegistrationEnabled = this.oldConnectionDetails.autoRegistrationEnabled;
            zoomMeetingDetails.autoSyncEnabled = this.oldConnectionDetails.autoSyncEnabled;
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError('Error', errorMessage);
            return;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }
        const newData = this.prePopulateFields(zoomMeetingDetails);
        this.updateZoomMeetingCallback(newData, false);
        if (this.editMode) {
            this.setDisableEditMode();
        }
        this.hideForm();
    };

    public getZoomDetails = async (connection: IConnectionDetailsDTO): Promise<void> => {
        try {
            const details = await this.zoomDAO.getZoomConnectionDetails(connection.connectionId);
            if (isZoomMeetingReccuring(details)) {
                this.isZoomIdInvalid = true;
                this.setEnableEditMode();
                this.onfetchZoomWrongId(connection, RECURRING_ZOOM_MEETING_ERROR);
                return;
            }
            this.setOldConnectionDetails(connection);
            this.setInitialValues(details);
            this.showForm();
            this.setEnableEditMode();
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            if (error?.response?.status === 404) {
                this.isZoomIdInvalid = true;
                this.setEnableEditMode();
                this.onfetchZoomWrongId(connection, errorMessage);
            } else {
                alertServerError('Error', errorMessage);
            }
        }
    };

    public createZoomMeetingManuallyHandle = async (showAutoRegAndAutoSyncToggles?: boolean): Promise<void> => {
        this.createZoomMeetingCallback(null, showAutoRegAndAutoSyncToggles);
        this.hideForm();
    };

    private prePopulateFields = (details): IConnectionDetailsDTO => {
        const { label, meetingConnectionId, connectionPassword,
            dialForVs, connectionNotes, connectionHostKey, meetingInvitation, connectionAccount, alternativeLink,
            connectionLink, sip, hostLink, meetingUniqueReference } = this.getOldConnectionDetails();
        return this.convertZoomMeetingInZoomConnection({
            ...details,
            label: label,
            dialForVs: dialForVs,
            connectionNotes: connectionNotes,
            connectionHostKey: connectionHostKey ?? details?.zoomUser?.hostKey,
            meetingConnectionId: meetingConnectionId,
            password: connectionPassword,
            invitation:  details?.invitation || meetingInvitation,
            connectionAccount: connectionAccount,
            alternativeLink: alternativeLink,
            connectionLink: connectionLink,
            hostLink: hostLink,
            sip: sip,
            meetingUniqueReference: meetingUniqueReference
        });
    };

    public updateZoomMeetingManuallyHandle = async (): Promise<void> => {
        const { meetingConnectionId } = this.getOldConnectionDetails();
        if (!this.isZoomIdInvalid) {
            const details = this.generalMeetingId ?
                await this.getUpdatedMeetingDetails(this.generalMeetingId, this.accountId, this.eventId, meetingConnectionId) :
                await this.getScheduledMeetingDetails(this.getMeetingId());
            details.autoRegistrationEnabled = this.oldConnectionDetails.autoRegistrationEnabled;
            details.autoSyncEnabled = this.oldConnectionDetails.autoSyncEnabled;
            const newData = this.prePopulateFields(details);
            await this.updateZoomMeetingCallback(newData, false);
        } else {
            this.isZoomIdInvalid = false;
            await this.updateZoomMeetingCallback(this.getOldConnectionDetails(), false);
        }
        if (this.editMode) {
            this.setDisableEditMode();
        }
        this.hideForm();
    };

    public getIsFormShown = (): boolean => {
        return this.isFormShown;
    };

    public getEditModeStatus = (): boolean => {
        return this.editMode;
    };

    public getIsFormSaving = (): boolean => {
        return this.isSaving;
    };

    public getIsRecurringMeetingFormSaving = (): boolean => {
        return this.isRecurringMeetingSaving;
    };

    private onfetchZoomWrongId = (connection: IConnectionDetailsDTO, errorMessage: string): void => {
        this.setOldConnectionDetails(connection);
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    {errorMessage}
                </span>
            ),
            confirmButtonProps: {
                text: <FormattedMessage
                    id='oe.common.alerts.editDetailsManually'
                    description='Button label'
                    defaultMessage='Edit Details Manually'
                />,
                modifier: 'confirm-warn',
                handler: () => {
                    this.updateZoomMeetingManuallyHandle();
                },
            },
            cancelButtonProps: {
                text: <FormattedMessage
                    id='oe.common.alerts.goBack'
                    description='Button label'
                    defaultMessage='Go Back'
                />,
                modifier: 'cancel',
                handler: () => {
                    this.isZoomIdInvalid = false;
                    this.hideForm();
                    this.setDisableEditMode();
                }
            }
        });
    };

    @mobx.action
    public onChangeZoomMeetingType = (value: ZoomMeetingType): void => {
        this.zoomMeetingType = value;
    };

    @mobx.action
    public fetchAccountAdmin = async (): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.isLoadingAccountAdmin = true;
            });
            const params = { accountId: this.accountId };

            const response = await this.accountAdminDAO.getAccountAdmin(params);
            const recurringZoomMeetingsData = response
                .find(value => value.type === AccountAdminSettingsType.RECURRING_ZOOM_MEETINGS) as IRecurringZoomMeetingsDTO;
            this.isRecurringZoomMeetingFormShown = recurringZoomMeetingsData.recurringZoomMeetingsEnabled;
            this.recurringZoomMeetingFormDataVM.setZoomGroupData(recurringZoomMeetingsData.allowedZoomGroups);
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError('Error', errorMessage);
        } finally {
            mobx.runInAction(() => {
                this.isLoadingAccountAdmin = false;
            });
        }
    };
}

export { ZoomMeetingVM };
